import React from "react";

/**
 * The Āvāhana wordmark, as outlines.
 *
 * These are real glyph outlines cut from Noto Sans SemiBold, not live text.
 * That matters: a wordmark rendered as <text> silently falls back to whatever
 * font the host has (the classic Satori/OG-image failure), cannot be stroked
 * for a draw-on, and re-flows if the font stack changes. Frozen outlines are
 * the same shape everywhere, forever.
 *
 * One path per glyph, so each letter can be drawn on in sequence — the word
 * writes itself left to right rather than fading in as a block.
 *
 * Noto Sans is not the UI font (that is Inter). A wordmark is a fixed brand
 * asset and is normally drawn from a different face than the interface type;
 * if you want it recut from Inter, regenerate from the font binary rather than
 * referencing the family by name.
 */
export const WORDMARK_VIEWBOX = "0 0 476.6 100";
export const WORDMARK_WIDTH = 476.6;
export const WORDMARK_HEIGHT = 100;

export const WORDMARK_GLYPHS: ReadonlyArray<{ ch: string; d: string }> = [
  { ch: "Ā", d: "M61.0 98.9 53.3 77.4H23.2L15.6 98.9H0.0L29.9 17.0H46.7L76.6 98.9ZM42.7 44.5Q42.2 43.2 41.3 40.1Q40.4 37.1 39.6 33.9Q38.7 30.7 38.2 28.7Q37.4 32.5 36.2 37.2Q34.9 41.8 34.1 44.5L27.1 64.8H49.7ZM56.4 0.0V10.0H20.0V0.0Z" },
  { ch: "v", d: "M100.2 98.9 76.7 37.0H92.0L104.1 73.5Q105.0 76.0 106.2 79.8Q107.3 83.6 107.5 86.0H108.0Q108.3 83.4 109.5 79.8Q110.6 76.1 111.5 73.5L123.9 37.0H139.2L115.5 98.9Z" },
  { ch: "ā", d: "M174.1 35.8Q197.4 35.8 197.4 57.3V98.9H187.1L184.4 90.3H183.9Q179.8 95.3 175.4 97.7Q171.0 100.0 163.2 100.0Q154.9 100.0 149.4 95.4Q143.9 90.8 143.9 80.9Q143.9 62.1 172.6 61.1L183.0 60.7V58.1Q183.0 51.8 180.1 49.3Q177.3 46.7 172.3 46.7Q167.8 46.7 163.4 48.1Q159.0 49.5 154.9 51.4L150.6 41.3Q155.1 38.9 161.1 37.4Q167.1 35.8 174.1 35.8ZM175.5 69.9Q166.1 70.2 162.4 72.9Q158.8 75.7 158.8 80.6Q158.8 85.0 161.4 87.1Q164.0 89.2 168.2 89.2Q174.4 89.2 178.7 85.6Q183.0 82.0 183.0 74.9V69.6ZM190.8 19.6V29.7H154.3V19.6Z" },
  { ch: "h", d: "M229.3 32.5Q229.3 36.6 229.1 39.8Q228.9 43.0 228.7 45.1H229.5Q232.4 40.3 237.2 38.1Q242.0 35.8 247.8 35.8Q258.3 35.8 264.3 41.2Q270.2 46.6 270.2 58.6V98.9H255.7V61.5Q255.7 47.5 244.4 47.5Q235.7 47.5 232.5 53.0Q229.3 58.6 229.3 68.7V98.9H214.8V12.1H229.3Z" },
  { ch: "a", d: "M313.7 35.8Q337.0 35.8 337.0 57.3V98.9H326.7L324.0 90.3H323.5Q319.4 95.3 315.0 97.7Q310.6 100.0 302.9 100.0Q294.5 100.0 289.0 95.4Q283.6 90.8 283.6 80.9Q283.6 62.1 312.2 61.1L322.6 60.7V58.1Q322.6 51.8 319.7 49.3Q316.9 46.7 311.9 46.7Q307.4 46.7 303.0 48.1Q298.6 49.5 294.5 51.4L290.2 41.3Q294.7 38.9 300.7 37.4Q306.7 35.8 313.7 35.8ZM315.1 69.9Q305.7 70.2 302.1 72.9Q298.4 75.7 298.4 80.6Q298.4 85.0 301.0 87.1Q303.7 89.2 307.8 89.2Q314.0 89.2 318.3 85.6Q322.6 82.0 322.6 74.9V69.6Z" },
  { ch: "n", d: "M387.7 35.8Q398.2 35.8 404.0 41.2Q409.8 46.6 409.8 58.6V98.9H395.4V61.5Q395.4 47.5 384.0 47.5Q375.3 47.5 372.1 53.0Q368.9 58.4 368.9 68.7V98.9H354.5V37.0H365.8L367.7 45.3H368.4Q371.3 40.6 376.4 38.2Q381.5 35.8 387.7 35.8Z" },
  { ch: "a", d: "M453.3 35.8Q476.6 35.8 476.6 57.3V98.9H466.3L463.6 90.3H463.1Q459.0 95.3 454.6 97.7Q450.2 100.0 442.5 100.0Q434.1 100.0 428.7 95.4Q423.2 90.8 423.2 80.9Q423.2 62.1 451.8 61.1L462.2 60.7V58.1Q462.2 51.8 459.4 49.3Q456.5 46.7 451.5 46.7Q447.0 46.7 442.6 48.1Q438.2 49.5 434.1 51.4L429.8 41.3Q434.4 38.9 440.4 37.4Q446.3 35.8 453.3 35.8ZM454.7 69.9Q445.3 70.2 441.7 72.9Q438.0 75.7 438.0 80.6Q438.0 85.0 440.6 87.1Q443.3 89.2 447.4 89.2Q453.7 89.2 457.9 85.6Q462.2 82.0 462.2 74.9V69.6Z" },
];

export interface AvahanaWordmarkProps
  extends React.SVGProps<SVGSVGElement> {
  /** Rendered height in px; width follows the aspect ratio. */
  height?: number;
  /** Applied to every glyph path — used by the splash to drive the draw-on. */
  glyphClassName?: string;
  /** Seconds between each letter starting to draw. */
  stagger?: number;
  /** Delay before the first letter starts, in seconds. */
  delay?: number;
  title?: string;
}

export function AvahanaWordmark({
  height = 20,
  glyphClassName,
  stagger = 0,
  delay = 0,
  title,
  ...rest
}: AvahanaWordmarkProps) {
  return (
    <svg
      viewBox={WORDMARK_VIEWBOX}
      height={height}
      width={(height * WORDMARK_WIDTH) / WORDMARK_HEIGHT}
      fill="currentColor"
      role={title ? "img" : undefined}
      aria-hidden={title ? undefined : true}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {WORDMARK_GLYPHS.map((g, i) => (
        <path
          key={i}
          d={g.d}
          pathLength={1}
          className={glyphClassName}
          style={
            stagger
              ? { animationDelay: `${(delay + i * stagger).toFixed(3)}s` }
              : undefined
          }
        />
      ))}
    </svg>
  );
}
